import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Bot, Plus, Trash2, Loader2, TrendingUp, TrendingDown, Activity, Pencil, Check } from 'lucide-react';
import { apiFetch } from '../utils/apiFetch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtUsd = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

// Brokers accepted by The DCA Club (CGTrade + RKX Financial)
export const MT_SERVERS = [
  { value: 'CGTrade-Demo',         broker: 'CGTrade LC Limited',         type: 'demo' },
  { value: 'CGTrade-Live',         broker: 'CGTrade LC Limited',         type: 'live' },
  { value: 'RKXFinancial-Demo',    broker: 'RKX Financial SA (Pty) Ltd', type: 'demo' },
  { value: 'RKXFinancial-Live',    broker: 'RKX Financial SA (Pty) Ltd', type: 'live' },
];

// AI bot identities — official brand colors + logos
export const BOT_CONFIG = {
  sigmai: {
    label: 'Sigmai',
    color: '#ff3030',
    glow: 'rgba(255,48,48,0.55)',
    logo: 'https://customer-assets.emergentagent.com/job_b81f51a6-c48a-4d97-bce8-746b02d5a794/artifacts/201eo5co_logo_sigmai.png',
    desc: 'Aggressive scalping AI',
  },
  sugair: {
    label: 'Sugair',
    color: '#22dd55',
    glow: 'rgba(34,221,85,0.55)',
    logo: 'https://customer-assets.emergentagent.com/job_b81f51a6-c48a-4d97-bce8-746b02d5a794/artifacts/4wfwv7tz_logo_sugair.png',
    desc: 'Adaptive swing AI',
  },
  jairvis: {
    label: 'Jairvis',
    color: '#00f4ff',
    glow: 'rgba(0,244,255,0.55)',
    logo: 'https://customer-assets.emergentagent.com/job_b81f51a6-c48a-4d97-bce8-746b02d5a794/artifacts/idlfkokn_Jairvis_logo.jpg',
    desc: 'Quant AI assistant',
  },
  custom: {
    label: 'Custom / Manual',
    color: '#9EB4C0',
    glow: 'rgba(158,180,192,0.4)',
    logo: null,
    desc: 'Manual or other strategy',
  },
};

const BotAvatar = ({ bot, size = 32 }) => {
  if (bot.logo) {
    return (
      <img
        src={bot.logo}
        alt={bot.label}
        style={{ width: size, height: size }}
        className="rounded-full object-cover ring-1"
      />
    );
  }
  // Fallback for "custom" — no logo, use initial in colored circle
  return (
    <div
      style={{
        width: size, height: size,
        background: `${bot.color}22`,
        border: `1.5px solid ${bot.color}`,
        color: bot.color,
        fontSize: size * 0.45,
      }}
      className="rounded-full flex items-center justify-center font-bold"
    >
      {bot.label[0]}
    </div>
  );
};

const MTModule = ({ onTotalEquity }) => {
  const [accounts, setAccounts] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ login: '', password: '', server: 'CGTrade-Live', platform: 'mt5', name: '', ai_bot: 'sigmai' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [editingBot, setEditingBot] = useState(null);

  const load = async () => {
    const r = await apiFetch(`${API}/mt/accounts`, { credentials: 'include' });
    const data = await r.json();
    const list = data.items || [];
    setAccounts(list);
    list.forEach(async (a) => {
      try {
        const mr = await apiFetch(`${API}/mt/accounts/${a.mt_id}/metrics`, { credentials: 'include' });
        const md = await mr.json();
        setMetrics((prev) => ({ ...prev, [a.mt_id]: md }));
      } catch {}
    });
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!onTotalEquity) return;
    let equity = 0, profit = 0, trades = 0, won = 0;
    const byBot = { sigmai: 0, sugair: 0, jairvis: 0, custom: 0 };
    accounts.forEach((a) => {
      const m = metrics[a.mt_id];
      if (!m) return;
      const eq = m.info?.equity || 0;
      equity += eq;
      profit += m.info?.profit || 0;
      trades += m.analytics?.trades || 0;
      won    += m.analytics?.wonTrades || 0;
      const key = (a.ai_bot || 'custom').toLowerCase();
      byBot[key] = (byBot[key] || 0) + eq;
    });
    const details = accounts.map((a) => {
      const m = metrics[a.mt_id];
      return {
        mt_id: a.mt_id,
        login: a.login,
        server: a.server,
        platform: a.platform,
        name: a.name,
        ai_bot: (a.ai_bot || 'custom').toLowerCase(),
        info: m?.info || {},
        analytics: m?.analytics || {},
        positions: m?.positions || [],
        mocked: !!m?.mocked,
      };
    });
    onTotalEquity(equity, {
      equity,
      profit,
      accounts: accounts.length,
      trades,
      winRate: trades > 0 ? (won / trades) * 100 : 0,
      byBot,
      details,
    });
  }, [metrics, accounts, onTotalEquity]);

  const handleAdd = async () => {
    setSubmitting(true);
    setError('');
    try {
      const r = await apiFetch(`${API}/mt/accounts`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const text = await r.text();
        // Try to parse MetaApi nested error
        let friendly = '';
        try {
          const outer = JSON.parse(text);
          const detail = typeof outer.detail === 'string' ? outer.detail : '';
          const inner = detail.replace(/^MetaApi:\s*/, '');
          const parsed = JSON.parse(inner);
          const msg = parsed?.message || parsed?.error || '';
          if (/top up/i.test(msg) || parsed?.error === 'ForbiddenError') {
            friendly = 'TOPUP'; // sentinel
          } else if (msg) {
            friendly = `MetaApi: ${msg}`;
          }
        } catch {}
        throw new Error(friendly || text.slice(0, 240));
      }
      setForm({ login: '', password: '', server: 'CGTrade-Live', platform: 'mt5', name: '', ai_bot: 'sigmai' });
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.message || 'Failed to add account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (mt_id) => {
    await apiFetch(`${API}/mt/accounts/${mt_id}`, { method: 'DELETE', credentials: 'include' });
    load();
  };

  const handleBotChange = async (mt_id, newBot) => {
    await apiFetch(`${API}/mt/accounts/${mt_id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ai_bot: newBot }),
    });
    setEditingBot(null);
    load();
  };

  // Per-bot aggregations
  const botSummary = ['sigmai', 'sugair', 'jairvis', 'custom'].map((key) => {
    const accs = accounts.filter((a) => (a.ai_bot || 'custom') === key);
    let equity = 0, profit = 0, trades = 0, won = 0;
    accs.forEach((a) => {
      const m = metrics[a.mt_id];
      if (m) {
        equity += m.info?.equity || 0;
        profit += m.info?.profit || 0;
        trades += m.analytics?.trades || 0;
        won += m.analytics?.wonTrades || 0;
      }
    });
    return { key, ...BOT_CONFIG[key], count: accs.length, equity, profit, trades, won, winRate: trades > 0 ? (won / trades) * 100 : 0 };
  }).filter((b) => b.count > 0);

  const accent = '#22c55e';

  return (
    <Card
      className="bg-[#040816] overflow-hidden"
      style={{ border: `1px solid ${accent}33`, boxShadow: `0 10px 40px -20px ${accent}55` }}
      data-testid="mt-module"
    >
      <div
        className="px-5 py-3 flex items-center justify-between"
        style={{ background: `linear-gradient(90deg, ${accent}1a, transparent 60%)`, borderBottom: `1px solid ${accent}22` }}
      >
        <div className="flex items-center gap-2.5">
          <Bot className="w-5 h-5" style={{ color: accent }} />
          <h3 className="text-base font-bold text-white">AI Trading Accounts</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm" onClick={() => setShowForm(!showForm)}
            className="bg-[#22c55e] hover:bg-[#16a34a] text-black font-semibold h-7 text-xs px-3"
            data-testid="mt-toggle-form-btn"
          >
            <Plus className="w-3 h-3 mr-1" /> {showForm ? 'Cancel' : 'Connect'}
          </Button>
          <span
            className="text-[10px] tracking-[0.25em] uppercase px-2 py-1 rounded-full"
            style={{ color: accent, backgroundColor: `${accent}18`, border: `1px solid ${accent}44` }}
          >
            Read-only
          </span>
        </div>
      </div>
      <CardContent className="p-5">
        {/* Per-bot summary chips (when ≥1 account exists) */}
        {botSummary.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="bot-summary">
            {botSummary.map((b) => (
              <div key={b.key}
                className="rounded-lg px-3 py-2 relative overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${b.color}18, ${b.color}05)`,
                  border: `1px solid ${b.color}44`,
                  boxShadow: `0 0 12px -4px ${b.glow}`,
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold tracking-[0.2em] uppercase flex items-center gap-1.5" style={{ color: b.color }}>
                    <BotAvatar bot={b} size={16} />
                    {b.label}
                  </span>
                  <span className="text-[9px] text-[#9EB4C0]">{b.count} acc</span>
                </div>
                <div className="text-base font-bold text-white mt-1" style={{ fontFamily: 'ui-monospace, monospace' }}>
                  {fmtUsd(b.equity)}
                </div>
                <div className="flex items-center justify-between text-[10px] mt-0.5">
                  <span className={b.profit >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}>
                    {b.profit >= 0 ? '+' : ''}{fmtUsd(b.profit)}
                  </span>
                  <span className="text-[#9EB4C0]">{b.winRate.toFixed(0)}% WR</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Connect form */}
        {showForm && (
          <div className="rounded-lg p-3 mb-4"
            style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)' }}
            data-testid="mt-form"
          >
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#22c55e] mb-2">
              Connect your MT4/5 account
            </div>

            {/* Bot selector — featured at top */}
            <div className="mb-3">
              <Label className="text-[10px] text-[#9EB4C0] mb-1.5 block">Which AI is running on this account?</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(BOT_CONFIG).map(([key, b]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm({ ...form, ai_bot: key })}
                    data-testid={`bot-select-${key}`}
                    className="text-left rounded-lg px-3 py-2 transition-all"
                    style={{
                      background: form.ai_bot === key ? `${b.color}22` : 'rgba(11,28,56,0.5)',
                      border: form.ai_bot === key ? `1.5px solid ${b.color}` : '1px solid rgba(255,255,255,0.08)',
                      boxShadow: form.ai_bot === key ? `0 0 14px -4px ${b.glow}` : 'none',
                    }}
                  >
                    <div className="text-base flex items-center justify-center" style={{ minHeight: 32 }}>
                      <BotAvatar bot={b} size={28} />
                    </div>
                    <div className="text-xs font-bold mt-1" style={{ color: form.ai_bot === key ? b.color : '#fff' }}>
                      {b.label}
                    </div>
                    <div className="text-[9px] text-[#9EB4C0] mt-0.5 leading-tight">{b.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-[#9EB4C0]">Login number</Label>
                <Input type="text" placeholder="12345678" value={form.login}
                  onChange={(e) => setForm({ ...form, login: e.target.value })}
                  className="bg-[#0b1c38] border-[#0b1c38] text-white h-9 text-sm" data-testid="mt-login" />
              </div>
              <div>
                <Label className="text-[10px] text-[#9EB4C0]">Investor password</Label>
                <Input type="password" placeholder="read-only password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="bg-[#0b1c38] border-[#0b1c38] text-white h-9 text-sm" data-testid="mt-password" />
              </div>
              <div>
                <Label className="text-[10px] text-[#9EB4C0]">Server</Label>
                <select value={form.server}
                  onChange={(e) => setForm({ ...form, server: e.target.value })}
                  className="w-full h-9 bg-[#0b1c38] border border-[#0b1c38] text-white text-sm rounded-md px-2"
                  data-testid="mt-server">
                  {MT_SERVERS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.value} — {s.broker}
                    </option>
                  ))}
                </select>
                <div className="text-[9px] text-[#556475] italic mt-0.5">
                  Only CGTrade & RKX Financial accounts are accepted.
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-[#9EB4C0]">Platform</Label>
                <select value={form.platform}
                  onChange={(e) => setForm({ ...form, platform: e.target.value })}
                  className="w-full h-9 bg-[#0b1c38] border border-[#0b1c38] text-white text-sm rounded-md px-2"
                  data-testid="mt-platform">
                  <option value="mt4">MT4</option>
                  <option value="mt5">MT5</option>
                </select>
              </div>
            </div>
            <Button onClick={handleAdd} disabled={submitting || !form.login || !form.password || !form.server}
              className="w-full mt-3 h-9 bg-[#22c55e] hover:bg-[#16a34a] text-black font-semibold" data-testid="mt-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Connect account
            </Button>
            {error && error === 'TOPUP' && (
              <div className="mt-3 rounded-lg p-3 border" style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.35)' }} data-testid="mt-error-topup">
                <div className="text-[12px] text-yellow-300 font-bold mb-1.5">
                  ⚡ MetaApi account needs credits
                </div>
                <div className="text-[11px] text-[#cfe1f0] leading-relaxed mb-2">
                  Your <strong className="text-white">MetaApi.cloud</strong> account (not the broker account) is out of credits. MetaApi charges a small fee per connected MT account.
                  Top up once (≈ $10 USD) and your accounts will connect immediately.
                </div>
                <a
                  href="https://app.metaapi.cloud/billing/credit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-yellow-400 text-black hover:brightness-110"
                  data-testid="mt-topup-link"
                >
                  Top up MetaApi.cloud →
                </a>
              </div>
            )}
            {error && error !== 'TOPUP' && (
              <div className="text-[11px] text-red-400 mt-2 break-all" data-testid="mt-error">{error}</div>
            )}
            <div className="text-[9px] text-[#556475] italic mt-2">
              🔒 We only store the MetaApi account ID and AI bot label. Your investor password is forwarded directly to MetaApi and never persisted in our database.
            </div>
          </div>
        )}

        {accounts.length === 0 && !showForm && (
          <div className="text-center py-6 px-2">
            <div className="text-sm text-[#9EB4C0] mb-2">
              Connect your MT4/5 broker account and tag it with the AI running on it (Sigmai, Sugair, Jairvis, or custom).
            </div>
            <div className="text-[10px] text-[#556475]">
              Powered by <a href="https://metaapi.cloud" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:underline">MetaApi.cloud</a>
            </div>
          </div>
        )}

        {/* Account cards — AI-first identification */}
        <div className="space-y-3">
          {accounts.map((a) => {
            const botKey = (a.ai_bot || 'custom').toLowerCase();
            const bot = BOT_CONFIG[botKey] || BOT_CONFIG.custom;
            const m = metrics[a.mt_id];
            const info = m?.info || {};
            const an = m?.analytics || {};
            const positions = m?.positions || [];
            const mocked = m?.mocked;
            const profit = info.profit || 0;
            const isEditing = editingBot === a.mt_id;
            return (
              <div key={a.mt_id}
                className="rounded-lg p-4 relative overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${bot.color}10, rgba(34,197,94,0.04))`,
                  border: `1px solid ${bot.color}44`,
                  boxShadow: `0 0 16px -8px ${bot.glow}`,
                }}
                data-testid={`mt-account-${a.login}`}
              >
                {/* AI-first header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1">
                    {/* Bot identity orb */}
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                      style={{
                        background: `radial-gradient(circle, ${bot.color}25, ${bot.color}05)`,
                        border: `1.5px solid ${bot.color}`,
                        boxShadow: `0 0 16px -2px ${bot.glow}`,
                      }}
                    >
                      <BotAvatar bot={bot} size={48} />
                    </div>
                    <div className="min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <select
                            value={botKey}
                            onChange={(e) => handleBotChange(a.mt_id, e.target.value)}
                            onBlur={() => setEditingBot(null)}
                            autoFocus
                            className="bg-[#0b1c38] border border-[#22c55e] text-white text-sm rounded h-7 px-2"
                            data-testid={`bot-edit-select-${a.login}`}
                          >
                            {Object.entries(BOT_CONFIG).map(([k, b]) => (
                              <option key={k} value={k}>{b.label}</option>
                            ))}
                          </select>
                          <button onClick={() => setEditingBot(null)} className="text-[#22c55e] p-1">
                            <Check className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingBot(a.mt_id)}
                          className="group flex items-center gap-1.5 text-left"
                          data-testid={`bot-edit-${a.login}`}
                        >
                          <div className="text-lg font-black tracking-tight"
                            style={{
                              color: bot.color,
                              textShadow: `0 0 12px ${bot.glow}`,
                            }}
                          >
                            {bot.label}
                          </div>
                          <Pencil className="w-3 h-3 text-[#556475] opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      )}
                      <div className="text-[10px] text-[#9EB4C0] font-mono leading-tight">
                        #{a.login} · {a.server} · {a.platform?.toUpperCase()}
                        {mocked && <span className="ml-2 text-yellow-400 uppercase tracking-wider px-1.5 py-0.5 bg-yellow-400/10 rounded text-[8px]">Mock</span>}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(a.mt_id)} className="text-[#ef4444] hover:text-red-300 flex-shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Metrics grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  <MetricCell label="Balance" value={info.balance !== undefined ? fmtUsd(info.balance) : '—'} />
                  <MetricCell label="Equity" value={info.equity !== undefined ? fmtUsd(info.equity) : '—'} color={bot.color} />
                  <MetricCell label="Profit" value={info.profit !== undefined ? `${profit >= 0 ? '+' : ''}${fmtUsd(profit)}` : '—'} color={profit >= 0 ? '#22c55e' : '#ef4444'} />
                  <MetricCell label="Free margin" value={info.freeMargin !== undefined ? fmtUsd(info.freeMargin) : '—'} />
                </div>

                {an.trades !== undefined && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                    <AnalyticsCell label="Win rate" value={`${(an.winRate || 0).toFixed(1)}%`} pct={an.winRate} color={bot.color} />
                    <AnalyticsCell label="Profit factor" value={(an.profitFactor || 0).toFixed(2)} highlight={an.profitFactor >= 1.5} />
                    <AnalyticsCell label="Max drawdown" value={`${(an.maxDrawdown || 0).toFixed(1)}%`} negative />
                    <AnalyticsCell label="Sharpe ratio" value={(an.sharpeRatio || 0).toFixed(2)} highlight={an.sharpeRatio >= 1} />
                  </div>
                )}

                {an.trades !== undefined && (
                  <div className="flex items-center justify-between text-[11px] text-[#9EB4C0] mb-2 flex-wrap gap-2">
                    <span className="flex items-center gap-1">
                      <Activity className="w-3 h-3" />
                      {an.trades} trades
                    </span>
                    <span className="text-[#22c55e] flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {an.wonTrades || 0} won
                    </span>
                    <span className="text-[#ef4444] flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" />
                      {an.lostTrades || 0} lost
                    </span>
                    <span>avg win: <span className="text-white">{fmtUsd(an.averageWin)}</span></span>
                    <span>avg loss: <span className="text-white">{fmtUsd(an.averageLoss)}</span></span>
                  </div>
                )}

                {positions.length > 0 && (
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#9EB4C0] mb-1.5">
                      Open positions ({positions.length})
                    </div>
                    <div className="space-y-1">
                      {positions.slice(0, 5).map((p) => {
                        const isBuy = (p.type || '').includes('BUY');
                        return (
                          <div key={p.id} className="grid grid-cols-5 gap-2 items-center text-[11px] px-2 py-1.5 rounded bg-white/5">
                            <span className={`font-bold ${isBuy ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
                              {isBuy ? 'BUY' : 'SELL'}
                            </span>
                            <span className="text-white font-mono">{p.symbol}</span>
                            <span className="text-[#9EB4C0]">{p.volume} lots</span>
                            <span className="text-[#9EB4C0]">@ {p.openPrice}</span>
                            <span className={`text-right ${p.profit >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
                              {p.profit >= 0 ? '+' : ''}{fmtUsd(p.profit)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!m && (
                  <div className="text-[10px] text-[#556475] italic text-center py-2">
                    Loading from MetaApi… (first sync may take 30-60s)
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const MetricCell = ({ label, value, color = '#fff' }) => (
  <div className="rounded px-2 py-1.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
    <div className="text-[9px] tracking-[0.2em] uppercase text-[#556475]">{label}</div>
    <div className="text-sm font-bold mt-0.5" style={{ color, fontFamily: 'ui-monospace, monospace' }}>
      {value}
    </div>
  </div>
);

const AnalyticsCell = ({ label, value, pct, highlight, negative, color }) => {
  let textColor = '#fff';
  if (highlight) textColor = '#22c55e';
  if (negative) textColor = '#ef4444';
  if (color && !negative && !highlight) textColor = color;
  return (
    <div className="rounded px-2 py-1.5" style={{ background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.1)' }}>
      <div className="text-[9px] tracking-[0.2em] uppercase text-[#556475]">{label}</div>
      <div className="text-sm font-bold mt-0.5" style={{ color: textColor, fontFamily: 'ui-monospace, monospace' }}>
        {value}
      </div>
      {pct !== undefined && (
        <div className="h-0.5 mt-1 bg-white/5 rounded">
          <div className="h-full rounded" style={{ width: `${Math.min(100, pct)}%`, background: textColor, boxShadow: `0 0 4px ${textColor}` }} />
        </div>
      )}
    </div>
  );
};

export default MTModule;
